import {
  bracketHasRound1Players,
  generateBracket,
  generateMatchList,
  getMatchListMatches,
  isMatchList,
  isMatchListDecided,
  refreshBracketFromParticipants,
  selectBracketWinner,
  setBracketMatchResult,
  shouldReuseBracketForParticipants,
  swapBracketSlots,
} from './bracket';
import { translate } from '../i18n';
import { translateDivisionLabel } from '../i18n/labels';

export const BRACKET_MODES = {
  COMBINED: 'combined',
  GENDER: 'gender',
  HEIGHT: 'height',
};

/** 경기 방식 (대회마다): 토너먼트 / 1:1 경기 여러 번 */
export const BRACKET_FORMATS = {
  TOURNAMENT: 'tournament',
  MATCHES: 'matches',
};

const MAX_HEIGHT_CM = 250;

export function isMatchFormat(bracketSet) {
  return bracketSet?.format === BRACKET_FORMATS.MATCHES;
}

/** 부문 대진 새로 만들기: 토너먼트 대진 또는 1:1 경기 자동 짝 */
function generateDivisionBracket(group, format) {
  return format === BRACKET_FORMATS.MATCHES ? generateMatchList(group) : generateBracket(group);
}

/** 저장된 부문 대진을 최신 참가자로 갱신 (경기 방식이 다르면 새로 만든다) */
function refreshDivisionBracket(existing, group, format) {
  if (existing && isMatchList(existing) === (format === BRACKET_FORMATS.MATCHES)) {
    return refreshBracketFromParticipants(existing, group);
  }
  return generateDivisionBracket(group, format);
}

/** 화면에 보여줄 부문인지: 1:1 경기는 경기가 없어도 부문을 남긴다 (편집에서 경기를 추가) */
function hasDivisionBracket(bracket) {
  return Boolean(bracket) && (isMatchList(bracket) || bracketHasRound1Players(bracket));
}

export function createHeightRangeId() {
  return `hr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function formatHeightRangeLabel(min, max) {
  return `${min}~${max}cm`;
}

export function parseParticipantHeight(participant) {
  const height = Number(participant?.height);
  return Number.isFinite(height) ? height : null;
}

export function splitParticipantsByGender(participants) {
  const male = participants.filter((p) => p.gender === 'M');
  const female = participants.filter((p) => p.gender === 'F');
  return { male, female };
}

export function getParticipantsInHeightRange(participants, range) {
  const min = Number(range.min);
  const max = Number(range.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];

  return participants.filter((participant) => {
    const height = parseParticipantHeight(participant);
    return height != null && height >= min && height <= max;
  });
}

export function normalizeHeightRanges(ranges) {
  if (!Array.isArray(ranges)) return [];

  return ranges.map((range) => ({
    id: range.id || createHeightRangeId(),
    min: Number(range.min),
    max: Number(range.max),
  }));
}

export function validateHeightRanges(ranges) {
  const normalized = normalizeHeightRanges(ranges);

  if (normalized.length === 0) {
    return { valid: false, message: translate('bracket.height.validation.required') };
  }

  for (let i = 0; i < normalized.length; i += 1) {
    const range = normalized[i];
    if (!Number.isFinite(range.min) || !Number.isFinite(range.max)) {
      return { valid: false, message: translate('bracket.height.validation.missingValues') };
    }
    if (range.min <= 0 || range.max > MAX_HEIGHT_CM) {
      return {
        valid: false,
        message: translate('bracket.height.validation.outOfBounds', { max: MAX_HEIGHT_CM }),
      };
    }
    if (range.min > range.max) {
      return { valid: false, message: translate('bracket.height.validation.minGreaterThanMax') };
    }
  }

  const sorted = [...normalized].sort((a, b) => a.min - b.min || a.max - b.max);
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const current = sorted[i];
    if (current.min <= prev.max) {
      return {
        valid: false,
        message: translate('bracket.height.validation.overlap'),
      };
    }
  }

  return { valid: true, ranges: normalized };
}

function buildHeightBracket(
  group,
  existingBracket,
  { forceRegenerate = false, format = BRACKET_FORMATS.TOURNAMENT } = {}
) {
  if (format === BRACKET_FORMATS.MATCHES) {
    // 1:1 경기는 참가자가 바뀌어도 기존 경기를 유지한다 (새 참가자는 편집에서 짝 추가)
    return forceRegenerate ? generateMatchList(group) : refreshDivisionBracket(existingBracket, group, format);
  }
  if (
    !forceRegenerate &&
    existingBracket &&
    !isMatchList(existingBracket) &&
    shouldReuseBracketForParticipants(existingBracket, group)
  ) {
    return refreshBracketFromParticipants(existingBracket, group);
  }
  return generateBracket(group);
}

function buildHeightDivisions(
  participants,
  heightRanges,
  existingDivisions = {},
  options = {}
) {
  const divisions = {};
  const validation = validateHeightRanges(heightRanges);
  if (!validation.valid) return divisions;

  validation.ranges.forEach((range) => {
    const group = getParticipantsInHeightRange(participants, range);
    if (group.length < 2) return;

    const existing = existingDivisions[range.id]?.bracket;
    const bracket = buildHeightBracket(group, existing, options);

    if (!hasDivisionBracket(bracket)) return;

    divisions[range.id] = {
      label: formatHeightRangeLabel(range.min, range.max),
      min: range.min,
      max: range.max,
      bracket,
    };
  });

  return divisions;
}

function pickSelectedHeightRange(divisions, heightRanges, previousId) {
  const available = heightRanges
    .map((range) => range.id)
    .filter((id) => {
      return hasDivisionBracket(divisions[id]?.bracket);
    });
  if (available.length === 0) return null;
  if (previousId && available.includes(previousId)) return previousId;
  return available[0];
}

export function getAvailableHeightRanges(bracketSet) {
  if (!bracketSet || bracketSet.mode !== BRACKET_MODES.HEIGHT) return [];

  return (bracketSet.heightRanges ?? [])
    .filter((range) => {
      return hasDivisionBracket(bracketSet.divisions?.[range.id]?.bracket);
    })
    .map((range) => ({
      id: range.id,
      label: formatHeightRangeLabel(range.min, range.max),
      min: range.min,
      max: range.max,
    }));
}

export function setBracketSetSelectedHeightRange(bracketSet, rangeId) {
  if (!bracketSet || bracketSet.mode !== BRACKET_MODES.HEIGHT) return bracketSet;
  const available = getAvailableHeightRanges(bracketSet).map((range) => range.id);
  if (!available.includes(rangeId)) return bracketSet;
  return { ...bracketSet, selectedHeightRangeId: rangeId };
}

export function applyHeightRangesToBracketSet(bracketSet, participants, heightRanges) {
  if (!bracketSet || bracketSet.mode !== BRACKET_MODES.HEIGHT) return bracketSet;

  const validation = validateHeightRanges(heightRanges);
  if (!validation.valid) return bracketSet;

  const divisions = buildHeightDivisions(participants, validation.ranges, bracketSet.divisions, {
    forceRegenerate: true,
    format: bracketSet.format,
  });

  return {
    ...bracketSet,
    heightRanges: validation.ranges,
    divisions,
    selectedHeightRangeId: pickSelectedHeightRange(
      divisions,
      validation.ranges,
      bracketSet.selectedHeightRangeId
    ),
  };
}

export function generateBracketSet(
  participants,
  mode = BRACKET_MODES.COMBINED,
  options = {}
) {
  if (!participants?.length || participants.length < 2) return null;
  const format = options.format ?? BRACKET_FORMATS.TOURNAMENT;

  if (mode === BRACKET_MODES.GENDER) {
    const { male, female } = splitParticipantsByGender(participants);
    const divisions = {};

    if (male.length >= 2) {
      divisions.M = { label: '남자부', bracket: generateDivisionBracket(male, format) };
    }
    if (female.length >= 2) {
      divisions.F = { label: '여자부', bracket: generateDivisionBracket(female, format) };
    }

    return {
      format,
      mode: BRACKET_MODES.GENDER,
      combined: null,
      divisions,
      heightRanges: [],
      selectedHeightRangeId: null,
    };
  }

  if (mode === BRACKET_MODES.HEIGHT) {
    const heightRanges = options.heightRanges ?? [];
    const validation = validateHeightRanges(heightRanges);
    const ranges = validation.valid ? validation.ranges : [];
    const divisions =
      ranges.length > 0
        ? buildHeightDivisions(participants, ranges, options.existingDivisions, { format })
        : {};

    return {
      format,
      mode: BRACKET_MODES.HEIGHT,
      combined: null,
      divisions,
      heightRanges: ranges,
      selectedHeightRangeId: pickSelectedHeightRange(
        divisions,
        ranges,
        options.selectedHeightRangeId
      ),
    };
  }

  return {
    format,
    mode: BRACKET_MODES.COMBINED,
    combined: generateDivisionBracket(participants, format),
    divisions: {},
    heightRanges: [],
    selectedHeightRangeId: null,
  };
}

export function getBracketSetDivisions(bracketSet) {
  if (!bracketSet) return [];

  if (bracketSet.mode === BRACKET_MODES.COMBINED && bracketSet.combined) {
    return [{ key: 'combined', label: null, bracket: bracketSet.combined }];
  }

  if (bracketSet.mode === BRACKET_MODES.HEIGHT) {
    const rangeId =
      bracketSet.selectedHeightRangeId ?? getAvailableHeightRanges(bracketSet)[0]?.id;
    const division = rangeId ? bracketSet.divisions?.[rangeId] : null;
    if (!division?.bracket) return [];
    return [{ key: rangeId, label: division.label, bracket: division.bracket }];
  }

  return ['M', 'F']
    .filter((key) => bracketSet.divisions?.[key]?.bracket)
    .map((key) => ({
      key,
      label: bracketSet.divisions[key].label,
      bracket: bracketSet.divisions[key].bracket,
    }));
}

export function refreshBracketSet(bracketSet, participants) {
  if (!bracketSet) return generateBracketSet(participants, BRACKET_MODES.COMBINED);

  const format = bracketSet.format ?? BRACKET_FORMATS.TOURNAMENT;
  const { male, female } = splitParticipantsByGender(participants);

  if (bracketSet.mode === BRACKET_MODES.GENDER) {
    const divisions = {};

    if (male.length >= 2) {
      divisions.M = {
        label: '남자부',
        bracket: refreshDivisionBracket(bracketSet.divisions?.M?.bracket, male, format),
      };
    }

    if (female.length >= 2) {
      divisions.F = {
        label: '여자부',
        bracket: refreshDivisionBracket(bracketSet.divisions?.F?.bracket, female, format),
      };
    }

    return {
      format,
      mode: BRACKET_MODES.GENDER,
      combined: null,
      divisions,
      heightRanges: [],
      selectedHeightRangeId: null,
    };
  }

  if (bracketSet.mode === BRACKET_MODES.HEIGHT) {
    const heightRanges = bracketSet.heightRanges ?? [];
    const divisions = buildHeightDivisions(participants, heightRanges, bracketSet.divisions, { format });

    return {
      format,
      mode: BRACKET_MODES.HEIGHT,
      combined: null,
      divisions,
      heightRanges,
      selectedHeightRangeId: pickSelectedHeightRange(
        divisions,
        heightRanges,
        bracketSet.selectedHeightRangeId
      ),
    };
  }

  return {
    format,
    mode: BRACKET_MODES.COMBINED,
    combined: refreshDivisionBracket(bracketSet.combined, participants, format),
    divisions: {},
    heightRanges: [],
    selectedHeightRangeId: null,
  };
}

export function selectBracketSetWinner(
  bracketSet,
  divisionKey,
  roundIndex,
  matchIndex,
  player
) {
  if (!bracketSet) return bracketSet;

  if (bracketSet.mode === BRACKET_MODES.COMBINED) {
    return {
      ...bracketSet,
      combined: selectBracketWinner(bracketSet.combined, roundIndex, matchIndex, player),
    };
  }

  const division = bracketSet.divisions?.[divisionKey];
  if (!division?.bracket) return bracketSet;

  return {
    ...bracketSet,
    divisions: {
      ...bracketSet.divisions,
      [divisionKey]: {
        ...division,
        bracket: selectBracketWinner(division.bracket, roundIndex, matchIndex, player),
      },
    },
  };
}

/** 경기 결과(라운드 점수 + 승자) 입력. result = { roundScores, winner } (setBracketMatchResult 참고) */
export function setBracketSetMatchResult(bracketSet, divisionKey, roundIndex, matchIndex, result) {
  if (!bracketSet) return bracketSet;

  if (bracketSet.mode === BRACKET_MODES.COMBINED) {
    return {
      ...bracketSet,
      combined: setBracketMatchResult(bracketSet.combined, roundIndex, matchIndex, result),
    };
  }

  const division = bracketSet.divisions?.[divisionKey];
  if (!division?.bracket) return bracketSet;

  return {
    ...bracketSet,
    divisions: {
      ...bracketSet.divisions,
      [divisionKey]: {
        ...division,
        bracket: setBracketMatchResult(division.bracket, roundIndex, matchIndex, result),
      },
    },
  };
}

export function swapBracketSetSlots(bracketSet, divisionKey, slotA, slotB) {
  if (!bracketSet) return bracketSet;

  if (bracketSet.mode === BRACKET_MODES.COMBINED) {
    return {
      ...bracketSet,
      combined: swapBracketSlots(bracketSet.combined, slotA, slotB),
    };
  }

  const division = bracketSet.divisions?.[divisionKey];
  if (!division?.bracket) return bracketSet;

  return {
    ...bracketSet,
    divisions: {
      ...bracketSet.divisions,
      [divisionKey]: {
        ...division,
        bracket: swapBracketSlots(division.bracket, slotA, slotB),
      },
    },
  };
}

/** 화면 선택과 상관없이 모든 부문 [{ key, label, bracket }] (키별 대진의 다른 구간 포함) */
export function getAllBracketSetDivisions(bracketSet) {
  if (!bracketSet) return [];
  if (bracketSet.mode === BRACKET_MODES.COMBINED) {
    return bracketSet.combined ? [{ key: 'combined', label: null, bracket: bracketSet.combined }] : [];
  }
  return Object.entries(bracketSet.divisions ?? {})
    .filter(([, division]) => division?.bracket)
    .map(([key, division]) => ({ key, label: division.label, bracket: division.bracket }));
}

export function getBracketSetSummaryLabel(bracketSet) {
  if (!bracketSet) return '';
  const matchFormat = isMatchFormat(bracketSet);
  const divisionSummary = (division) =>
    translate(matchFormat ? 'bracket.summary.matchesDivision' : 'bracket.summary.division', {
      label: translateDivisionLabel(division.label),
      size: division.bracket.bracketSize,
      count: division.bracket.bracketSize,
    });

  if (bracketSet.mode === BRACKET_MODES.COMBINED && bracketSet.combined) {
    return matchFormat
      ? translate('bracket.summary.matches', { count: bracketSet.combined.bracketSize })
      : translate('bracket.summary.combined', { size: bracketSet.combined.bracketSize });
  }

  if (bracketSet.mode === BRACKET_MODES.HEIGHT) {
    const division = getBracketSetDivisions(bracketSet)[0];
    if (!division) {
      const rangeCount = bracketSet.heightRanges?.length ?? 0;
      return rangeCount > 0
        ? translate('bracket.summary.heightNeedsPlayers')
        : translate('bracket.summary.heightNeedsRanges');
    }
    return divisionSummary(division);
  }

  const parts = getBracketSetDivisions(bracketSet).map(divisionSummary);
  return parts.length > 0 ? parts.join(' · ') : translate('bracket.summary.genderNotEnough');
}

export function normalizeStoredBracketData(data, participants) {
  if (!data) return null;
  if (data.bracketSet) {
    return refreshBracketSet(data.bracketSet, participants);
  }
  if (data.bracket) {
    return refreshBracketSet(
      {
        mode: BRACKET_MODES.COMBINED,
        combined: data.bracket,
        divisions: {},
        heightRanges: [],
        selectedHeightRangeId: null,
      },
      participants
    );
  }
  return null;
}

/** 부문 대진을 바꾼다: update(bracket) → 새 bracket (1:1 경기 편집 등) */
export function updateBracketSetDivision(bracketSet, divisionKey, update) {
  if (!bracketSet) return bracketSet;

  if (bracketSet.mode === BRACKET_MODES.COMBINED) {
    return bracketSet.combined ? { ...bracketSet, combined: update(bracketSet.combined) } : bracketSet;
  }

  const division = bracketSet.divisions?.[divisionKey];
  if (!division?.bracket) return bracketSet;

  return {
    ...bracketSet,
    divisions: {
      ...bracketSet.divisions,
      [divisionKey]: { ...division, bracket: update(division.bracket) },
    },
  };
}

/** 부문에 들어가는 참가자 (통합: 전체, 남녀: 성별, 키별: 키 구간) */
export function getDivisionParticipants(bracketSet, divisionKey, participants) {
  if (!bracketSet || !participants) return [];
  if (bracketSet.mode === BRACKET_MODES.GENDER) {
    return participants.filter((p) => p.gender === divisionKey);
  }
  if (bracketSet.mode === BRACKET_MODES.HEIGHT) {
    const division = bracketSet.divisions?.[divisionKey];
    return division ? getParticipantsInHeightRange(participants, division) : [];
  }
  return participants;
}

/** 결과 확정 가능: 토너먼트는 모든 부문 우승자, 1:1 경기는 모든 경기 승패 */
export function canFinalizeBracketSet(bracketSet) {
  const brackets = getAllBracketSetDivisions(bracketSet).map((division) => division.bracket);
  if (brackets.length === 0) return false;
  if (isMatchFormat(bracketSet)) {
    const withMatches = brackets.filter((bracket) => getMatchListMatches(bracket).length > 0);
    return withMatches.length > 0 && withMatches.every(isMatchListDecided);
  }
  return brackets.every((bracket) => bracket.champion);
}

/** 화면에 보여줄 경기 방식 이름 (i18n 키) */
export function getBracketFormatLabelKey(bracketSet) {
  const matchFormat = isMatchFormat(bracketSet);
  if (bracketSet?.mode === BRACKET_MODES.GENDER) {
    return matchFormat ? 'tournament.mode.matchesGender' : 'tournament.mode.gender';
  }
  if (bracketSet?.mode === BRACKET_MODES.HEIGHT) {
    return matchFormat ? 'tournament.mode.matchesHeight' : 'tournament.mode.height';
  }
  return matchFormat ? 'tournament.mode.matches' : 'tournament.mode.single';
}
